import React, { useContext } from "react";
import { Drawer, Box, Typography, IconButton, Button, Link, Fade } from "@mui/material";
import { PureLightThemeCaaS } from "src/theme/schemes/PureLightTheme";
import { isIOS, isAndroid } from "src/utils/platformUtils";
import styles from "../../content/pages/homepage.module.css";
import CloseIcon from "@mui/icons-material/Close";
import benchImage from "../../assets/bench.png";
import { Browser } from "@capacitor/browser";
import { useAuth0 } from "@auth0/auth0-react";
import { mobile, isMobileWebBrowser } from "src/utils/platformUtils";
import { WidgetContext } from "src/contexts/WidgetContext";
import UpgradeSubscriptionFree from "../PaymentMethod/UpgradeSubscriptionFree";
import { formatDateTimeUserTimezone } from "src/utils/dateUtils";
import BenchUpgradeSubscriptionFree from "./BenchUpgradeSubscriptionFree";
import BenchButtonsToShow from "./BenchButtonsToShow";
import { markAnalyticsSignupIntent, trackSignupScreenSeenAt } from "src/utils/analyticsCapFirebase";
//importar el rotogpt icon aqui por favor
import rotoGPTIcon from "../../assets/RotoGPTblack.png";

interface BenchDrawerProps {
    open: boolean;
    onClose: () => void;
    setIsPaymentOpen: (open: boolean) => void;
    setInitialPaymentView: (view: string) => void;
}

const BenchDrawer: React.FC<BenchDrawerProps> = ({ open, onClose, setIsPaymentOpen, setInitialPaymentView }) => {
    const { isLoggedInAuth0ExperienceGlobal, NON_LOGGED_IN_CONFIG, nextFreeCreditsResetTime, aiSettings } = useContext(WidgetContext)
    const { loginWithRedirect } = useAuth0();
    const isMobile = isMobileWebBrowser() || mobile;

    // Function to handle the login - replicates GetStartedPage login flow
    const handleSignUpForFree = async (event: React.MouseEvent) => {
        // Stop the event propagation
        event.stopPropagation();

        // Login with redirect - exact same logic as GetStartedPage
        markAnalyticsSignupIntent();
        await trackSignupScreenSeenAt("locked_feature", "bench");
        await loginWithRedirect({
            async openUrl(url) {
                // Redirect using Capacitor's Browser plugin
                await Browser.open({
                    url,
                    windowName: "_self",
                });
            },
        });
    };

    return (
        <Drawer
            anchor="bottom"
            open={open}
            onClose={onClose}
            hideBackdrop={false}
            sx={{ pointerEvents: "none" }}
            ModalProps={{
                keepMounted: true,
                sx: {
                    pointerEvents: "auto", // Block background scroll when drawer is open
                    top: isIOS || isAndroid ? "calc(env(safe-area-inset-top) + 56px)" : "56px",
                    zIndex: 1450, // medium priority: BenchDrawer appears above response drawers but below PaymentMethod
                },
            }}
            PaperProps={{
                sx: {
                    pointerEvents: "auto",
                    width: {
                        xs: "100%",
                        sm: "720px",
                        md: "720px",
                        lg: "720px",
                      },
                      margin: {
                        xs: "0",
                        sm: "0 auto",
                        md: "0 auto",
                        lg: "0 auto",
                      },
                    // height: isIOS || isAndroid ? "calc(100svh - env(safe-area-inset-top) - 56px)" : "calc(100vh - 56px)",
                    height: isIOS || isAndroid
                        ? (aiSettings.quan ? "calc(100svh - env(safe-area-inset-top))" : "calc(100svh - env(safe-area-inset-top) - 56px)")
                        : (!aiSettings.quan ? "calc(100vh - 56px)" : "100%"), border: `1px solid ${PureLightThemeCaaS.colors.alpha.white[30]}`,
                    borderTopLeftRadius: "10px",
                    borderTopRightRadius: "10px",
                    bgcolor: "#EDEDED",
                    zIndex: 1450, // medium priority: BenchDrawer appears above response drawers but below PaymentMethod
                },
            }}
        >
            <Fade in={open}>
                <Box
                    className={styles.noScrollbar}
                    sx={{
                        bgcolor: "#ffffff",
                        opacity: open ? 1 : 0,
                        transition: "opacity 0.5s",
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        overflow: "hidden",
                    }}
                >
                    {/* Header with title and close button - Fixed at top */}
                    <Box
                        sx={{
                            p: 1,
                            display: "flex",
                            flexDirection: "row",
                            justifyContent: "space-between",
                            alignItems: "center",
                            flexShrink: 0,
                            borderBottom: `1px solid ${PureLightThemeCaaS.colors.alpha.black[100]}`,
                            px: 1,
                            position: "sticky",
                            top: 0,
                            zIndex: 10,
                            bgcolor: "#EDEDED",
                        }}
                    >
                        <IconButton
                            sx={{ visibility: "hidden" }}
                        >
                            <CloseIcon />
                        </IconButton>
                        <Typography
                            variant="h4"
                            sx={{
                                color: `${PureLightThemeCaaS.colors.alpha.black[100]}`,
                                fontSize: "16px",
                                flex: 1,
                                textAlign: "center",
                                fontWeight: "bold",
                            }}
                        >
                            Limit Reached
                        </Typography>
                        <IconButton
                            aria-label="close"
                            onClick={(e) => {
                                if (e.currentTarget) {
                                    e.currentTarget.blur();
                                }
                                onClose();
                            }}
                            sx={{
                                color: `${PureLightThemeCaaS.colors.alpha.black[100]}`,
                                width: 40,
                            }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    {/* Content area - Scrollable */}
                    <Box
                        sx={{
                            flex: 1,
                            overflowY: "auto",
                            overflowX: "hidden",
                            "&::-webkit-scrollbar": {
                                display: "none",
                            },
                            scrollbarWidth: "none",
                            msOverflowStyle: "none",
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "flex-start",
                                pt: 0, // No top padding to minimize space above image
                                // pb: isAndroid ? "max(env(safe-area-inset-bottom), 15px)" : "15px", // Move paddingBottom here to avoid dark color at bottom
                                bgcolor: "#FFFFFF",
                                borderRadius: 0,
                            }}
                        >
                            {/* Bench image */}
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    overflow: "hidden",
                                    width: "100%",
                                    maxWidth: isMobile ? "200px" : "250px",
                                    height: "auto", // Fixed height to crop whitespace
                                    // mt: -1, // Negative margin to reduce space above image
                                    my: 4,
                                }}
                            >
                                <Box
                                    component="img"
                                    src={rotoGPTIcon}
                                    alt="Bench illustration"
                                    sx={{
                                        width: "100%",
                                        height: "100%",
                                        // objectFit: "cover", // Crop to fill container
                                        // objectPosition: "center", // Center the visible content
                                    }}
                                />
                            </Box>

                            {/* Main title */}
                            <Typography
                                variant="h4"
                                sx={{
                                    color: `${PureLightThemeCaaS.colors.alpha.black[100]}`,
                                    fontSize: "22px",
                                    fontWeight: "bold",
                                    textAlign: "center",
                                    pb: 1,
                                }}
                            >
                                Sign up for 50 free credits. No credit card required
                            </Typography>

                            {/* Description text */}
                            {/* <Typography
                                variant="body1"
                                sx={{
                                    color: `${PureLightThemeCaaS.colors.alpha.black[100]}`,
                                    fontSize: "15px",
                                    textAlign: "center",
                                    fontWeight: "medium",
                                    pb: 1,

                                    // lineHeight: 1.2,
                                }}
                            >
                                You've hit the daily analysis limit for free accounts.

                            </Typography>
                            <Typography
                                variant="body1"
                                sx={{
                                    color: `${PureLightThemeCaaS.colors.alpha.black[100]}`,
                                    fontSize: "15px",
                                    textAlign: "center",
                                    fontWeight: "medium",
                                    pb: 1,
                                }}
                            >

                                Your cooldown ends {nextFreeCreditsResetTime ? formatDateTimeUserTimezone(nextFreeCreditsResetTime) : "tomorrow"}.
                            </Typography> */}

                            {/* Question text */}
                            <Typography
                                variant="body1"
                                sx={{
                                    color: `${PureLightThemeCaaS.colors.alpha.black[100]}`,
                                    fontSize: "16px",
                                    textAlign: "center",
                                    pb: 2,
                                    fontWeight: "medium",
                                }}
                            >
                                Need back in the game right now?
                            </Typography>

                            {/* Upgrade Subscription Free button for logged in users and Sign Up For Free button for non-logged in users */}
                            {isLoggedInAuth0ExperienceGlobal ? (
                                <><BenchUpgradeSubscriptionFree setIsPaymentOpen={setIsPaymentOpen} setInitialPaymentView={setInitialPaymentView} /></>
                            ) : (
                                // Buttons to show if the user is not logged in or logged in
                                <><BenchButtonsToShow handleSignUpForFree={handleSignUpForFree} /></>
                            )}

                            {/* Wait until reset time link */}
                            {/* <Link
                                component="button"
                                onClick={onClose}
                                sx={{
                                    pt: 1,
                                    color: `${PureLightThemeCaaS.colors.alpha.black[100]}`,
                                    fontSize: "14px",
                                    textDecoration: "underline",
                                    textTransform: "none",
                                    cursor: "pointer",
                                    "&:hover": {
                                        textDecoration: "underline",
                                    },
                                }}
                            >
                                Wait until reset
                            </Link> */}
                        </Box>
                    </Box>
                </Box>
            </Fade>
        </Drawer>
    );
};

export default BenchDrawer;
